import { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { Navigate, useNavigate } from 'react-router';

import StripeProvider from './StripeProvider';
import type { OrderOrder, OrderResponsePayment } from '../../types/codexOrder';
import PaymentInfo from '../Commons/PaymentInfo';
import { OrderStatus } from '../../constants/OrderStatus';
import { capturePaymentOnPg } from '../../services/api';
import {
  ClockIcon,
  FailedIcon,
  SuccessIcon,
  LoadingIcon,
} from '../../assets/Icons';
import UpButton from '../../components/UpButton';

const STRIPE_PAYMENT_STATUS = {
  LOADING: {
    key: 'loading',
    primary: 'Processing payment',
    secondary: 'Please wait. Do not refresh or close this tab.',
    icon: <LoadingIcon className="text-brand-icon w-20 h-20 mx-auto" />,
  },
  SUCCESS: {
    key: 'succeeded',
    primary: 'Payment successful!',
    secondary: 'Your order has been placed.',
    icon: <SuccessIcon className="text-up-green-70 w-20 h-20 mx-auto" />,
  },
  IN_PROGRESS: {
    key: 'processing',
    primary: 'Processing payment',
    secondary: 'Payment is being processed on gateway.',
    icon: <ClockIcon className="text-brand-icon w-20 h-20 mx-auto" />,
  },
  FAILED: {
    key: 'requires_payment_method',
    primary: 'Payment failed!',
    secondary: 'Please try again or change your payment method.',
    icon: <FailedIcon className="text-up-red-70 w-20 h-20 mx-auto" />,
  },
  ERROR: {
    key: 'error',
    primary: 'Payment error!',
    secondary: 'Something went wrong. Please try again later.',
    icon: <FailedIcon className="text-up-red-70 w-20 h-20 mx-auto" />,
  },
  CANCELED: {
    key: 'canceled',
    primary: 'Payment canceled!',
    secondary: 'Payment gateway link is canceled or expired.',
    icon: <FailedIcon className="text-up-red-70 w-20 h-20 mx-auto" />,
  },
  ACTION_REQUIRED: {
    key: 'requires_action',
    primary: 'Payment requires action!',
    secondary: 'Please try again or change your payment method.',
    icon: <FailedIcon className="text-up-red-70 w-20 h-20 mx-auto" />,
  },
};

function PaymentStatus({
  order,
  orderId,
  payment,
  setStartRefetch,
}: {
  order: OrderOrder;
  orderId: string;
  payment: OrderResponsePayment | undefined;
  setStartRefetch: (value: boolean) => void;
}) {
  if (!payment) {
    return <Navigate to={`/${orderId}`} replace />;
  }

  return (
    <StripeProvider
      publishKey={payment?.key}
      clientSecret={payment?.data?.stripe_client_secret}>
      <StatusCheck
        order={order}
        orderId={orderId}
        setStartRefetch={setStartRefetch}
        clientSecret={payment?.data?.stripe_client_secret}
      />
    </StripeProvider>
  );
}

export default PaymentStatus;

function StatusCheck({
  order,
  orderId,
  clientSecret,
  setStartRefetch,
}: {
  order: OrderOrder;
  orderId: string;
  clientSecret: string;
  setStartRefetch: (value: boolean) => void;
}) {
  const stripe = useStripe();
  const navigate = useNavigate();
  const transactionId = order?.payment?.[0]?.srvr_trx_id;
  const [status, setStatus] = useState(STRIPE_PAYMENT_STATUS.LOADING.key);

  useEffect(() => {
    if (
      status === STRIPE_PAYMENT_STATUS.SUCCESS.key &&
      order?.details?.order_state !== OrderStatus.AWAITING_PAYMENT
    ) {
      setTimeout(() => {
        void navigate(`/${orderId}`, { replace: true });
      }, 2 * 1000);
    }
  }, [navigate, order?.details?.order_state, orderId, status]);

  useEffect(() => {
    if (!stripe || !clientSecret) {
      return;
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        const paymentStatus = paymentIntent?.status;
        if (paymentStatus) {
          if (
            transactionId &&
            paymentStatus === STRIPE_PAYMENT_STATUS.SUCCESS.key &&
            order?.details?.order_state === OrderStatus.AWAITING_PAYMENT
          ) {
            capturePaymentOnPg(transactionId)
              .then(() => {
                setStartRefetch(true);
                setStatus(paymentStatus);
              })
              .catch((e: unknown) => {
                console.log(e);
                setStatus(STRIPE_PAYMENT_STATUS.ERROR.key);
              });
          } else {
            setStatus(paymentStatus);
          }
        }
      })
      .catch((err: unknown) => {
        console.log(err);
        setStatus(STRIPE_PAYMENT_STATUS.ERROR.key);
      });
  }, [
    stripe,
    clientSecret,
    transactionId,
    setStartRefetch,
    order?.details?.order_state,
  ]);

  return <StatusUI status={status} orderId={orderId} />;
}

export function StatusUI({
  status,
  orderId,
}: {
  status: string;
  orderId?: string;
}) {
  const navigate = useNavigate();
  const info = Object.values(STRIPE_PAYMENT_STATUS)?.find(
    s => s?.key === status,
  );

  if (!info) {
    return null;
  }

  return (
    <div>
      <div className="text-center py-14 px-6 bg-white mx-8 mt-26 rounded-xl">
        {info?.icon}
        <p className="text-lg font-bold mt-6 mb-4">{info?.primary}</p>
        <p className="w-4/5 text-sm text-up-black-50 text-center mx-auto leading-6">
          {info?.secondary}
        </p>
        {orderId &&
          [
            STRIPE_PAYMENT_STATUS.FAILED.key,
            STRIPE_PAYMENT_STATUS.ACTION_REQUIRED.key,
          ].includes(status) && (
            <UpButton
              className="mt-6 max-w-72"
              onClick={() => void navigate(`/${orderId}`, { replace: true })}>
              Retry payment
            </UpButton>
          )}
        {status === STRIPE_PAYMENT_STATUS.SUCCESS.key && (
          <p className="text-sm text-up-black-50 text-center mx-auto mt-5">
            Redirecting...
          </p>
        )}
      </div>
      <PaymentInfo />
    </div>
  );
}
